/**
 * Hizmet bölgeleri (ADR 0113 §3): kaba ve sabit bir denizcilik bölgesi listesi. Tedarikçi hizmet
 * verdiği bölgeleri seçer, ofis RFQ'nun teslim bölgesini seçer; açık RFQ'yu bağlı olmayan
 * tedarikçilerden yalnız bölgesi eşleşenler görür; ofis davet ederken tedarikçileri bölgeye göre
 * süzer. Bağımlılığı yoktur (tedarikçi kartı şeması da kullanır).
 */

export const SERVICE_REGIONS = [
  'north_europe',
  'west_med',
  'east_med',
  'black_sea',
  'middle_east',
  'indian_ocean',
  'southeast_asia',
  'far_east',
  'oceania',
  'west_africa',
  'southern_africa',
  'north_america',
  'caribbean_central_america',
  'south_america',
] as const;
export type ServiceRegion = (typeof SERVICE_REGIONS)[number];

/**
 * Ülkenin (ISO 3166-1 alfa-2) varsayılan bölgesi: ofisin "teslim bölgesi" ve tedarikçinin kayıt
 * formu bununla önerilir, kişi değiştirebilir. Birden çok denize kıyısı olan ve tek bölgeye
 * yazılamayan ülke (ör. RU) listede yoktur; bölge elle seçilir.
 */
export const REGION_COUNTRIES: Readonly<Record<ServiceRegion, readonly string[]>> = {
  north_europe: [
    'NO',
    'SE',
    'DK',
    'FI',
    'IS',
    'DE',
    'NL',
    'BE',
    'GB',
    'IE',
    'PL',
    'EE',
    'LV',
    'LT',
    'FO',
    'GL',
  ],
  west_med: ['ES', 'PT', 'FR', 'IT', 'MT', 'MC', 'GI', 'MA', 'DZ', 'TN'],
  east_med: ['GR', 'CY', 'TR', 'IL', 'LB', 'SY', 'EG', 'LY', 'HR', 'SI', 'ME', 'AL', 'BA'],
  black_sea: ['BG', 'RO', 'UA', 'GE', 'MD'],
  middle_east: ['SA', 'AE', 'QA', 'BH', 'KW', 'OM', 'IR', 'IQ', 'YE', 'JO', 'DJ', 'ER', 'SD'],
  indian_ocean: ['IN', 'PK', 'LK', 'BD', 'MV', 'KE', 'TZ', 'MZ', 'MG', 'MU', 'SC', 'SO', 'RE'],
  southeast_asia: ['SG', 'MY', 'ID', 'TH', 'VN', 'PH', 'MM', 'KH', 'BN', 'TL'],
  far_east: ['CN', 'HK', 'TW', 'KR', 'JP', 'MO'],
  oceania: ['AU', 'NZ', 'PG', 'FJ', 'NC', 'PF'],
  west_africa: [
    'NG',
    'GH',
    'CI',
    'SN',
    'TG',
    'BJ',
    'CM',
    'GA',
    'AO',
    'CG',
    'CD',
    'MR',
    'GN',
    'SL',
    'LR',
    'GQ',
    'CV',
    'GM',
    'GW',
  ],
  southern_africa: ['ZA', 'NA'],
  north_america: ['US', 'CA'],
  caribbean_central_america: [
    'MX',
    'PA',
    'CU',
    'DO',
    'JM',
    'BS',
    'TT',
    'PR',
    'CR',
    'GT',
    'HN',
    'NI',
    'BZ',
    'CO',
    'VE',
    'HT',
    'BB',
    'CW',
    'AW',
  ],
  south_america: ['BR', 'AR', 'UY', 'CL', 'PE', 'EC', 'PY', 'SR', 'GY'],
};

const COUNTRY_REGION = new Map<string, ServiceRegion>(
  Object.entries(REGION_COUNTRIES).flatMap(([region, countries]) =>
    countries.map((country) => [country, region as ServiceRegion] as const),
  ),
);

/** Ülkenin varsayılan bölgesi; yoksa `null` (elle seçilir). */
export function regionOfCountry(country: string | null | undefined): ServiceRegion | null {
  return COUNTRY_REGION.get((country ?? '').trim().toUpperCase()) ?? null;
}

export function isServiceRegion(value: unknown): value is ServiceRegion {
  return (SERVICE_REGIONS as readonly unknown[]).includes(value);
}

/** Tedarikçi bölgeye hizmet veriyor mu (ofisin bölge süzgeci ve açık RFQ görünürlüğü). */
export function supplierServesRegion(
  regions: readonly string[] | null | undefined,
  region: string | null | undefined,
): boolean {
  return region != null && (regions ?? []).includes(region);
}
